import { t } from '@lingui/macro';
import type { ApiClient } from '@prismo-io/schemas';
import { z } from 'zod';
import { SkillTypeEnum } from './skill-type-enum';

const ParamsSchema = z.object({
  id: z.string().uuid(),
  type: SkillTypeEnum,
  level: z.number().int().nullable(),
  interest: z.number().int().nullable(),
  favorite: z.number().int().nullish(),
  description: z.string().nullish(),
});

export type ParamsSchemaT = z.infer<typeof ParamsSchema>;

const ResultSchema = z.boolean();

export type ResultSchemaT = z.infer<typeof ResultSchema>;

export const updateSkill = async (
  client: ApiClient,
  params: ParamsSchemaT
): Promise<ResultSchemaT> => {
  const {
    data: { session },
  } = await client.auth.getSession();

  if (!session) {
    throw new Error('You must be connected');
  }

  const userId = session.user.id;
  const { id, type, level, interest, favorite, description } =
    ParamsSchema.parse(params);

  if (favorite) {
    const { data: skill } = await client
      .from('quark_users_skills')
      .select('id')
      .eq('favorite', favorite)
      .eq('user_id', userId)
      .limit(1)
      .maybeSingle()
      .throwOnError();

    if (skill) {
      await client
        .from('quark_users_skills')
        .update({
          favorite: null,
        })
        .eq('id', skill.id)
        .throwOnError();
    }

    const { data: knowledge } = await client
      .from('quark_users_knowledges')
      .select('id')
      .eq('favorite', favorite)
      .eq('user_id', userId)
      .limit(1)
      .maybeSingle()
      .throwOnError();

    if (knowledge) {
      await client
        .from('quark_users_knowledges')
        .update({
          favorite: null,
        })
        .eq('id', knowledge.id)
        .throwOnError();
    }
  }

  switch (type) {
    case SkillTypeEnum.Enum.SKILLS:
      await client
        .from('quark_users_skills')
        .update({
          is_known: level && level > -1 ? true : false,
          is_wish: interest && interest > -1 ? true : false,
          level: level ?? -1,
          interest: interest ?? -1,
          favorite: favorite,
          description: description,
        })
        .eq('id', id)
        .throwOnError();

      break;

    case SkillTypeEnum.Enum.KNOWLEDGE:
      await client
        .from('quark_users_knowledges')
        .update({
          is_known: level && level > -1 ? true : false,
          is_wish: interest && interest > -1 ? true : false,
          level: level ?? -1,
          interest: interest ?? -1,
          favorite: favorite,
          description: description,
        })
        .eq('id', id)
        .throwOnError();

      break;

    default:
      throw new Error(t`Le type ne correspond à aucun skill`);
      break;
  }

  return ResultSchema.parse(true);
};
